import { useState, useEffect } from 'react';
import { fetchTweetContent, summarizeText, generateTrollSummary, generateMemeText, generateMemeImage } from './utils';
import { copyImageToClipboard, downloadCard, VisualCard } from './VisualCard';
import { useGoogleAnalytics, event } from './GoogleAnalytics';
import ThreadGenerator from './ThreadGenerator'; // Import the new component

function App() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState('');
  const [profileImage, setProfileImage] = useState('');
  const [username, setUsername] = useState('');
  const [tweetText, setTweetText] = useState('');
  const [style, setStyle] = useState('meme'); // Default to meme mode for UI
  const [memeImage, setMemeImage] = useState(null);
  
  // Store summaries for each mode to avoid regenerating when switching
  const [normalSummary, setNormalSummary] = useState('');
  const [trollSummary, setTrollSummary] = useState('');
  const [memeSummary, setMemeSummary] = useState('');
  const [memeImageCache, setMemeImageCache] = useState(null);
  
  // NEW: Track the current mode of the displayed card content
  const [currentCardMode, setCurrentCardMode] = useState(null);

  useGoogleAnalytics();

  const generateCard = async () => {
    setLoading(true);
    try {
      event({ action: 'generate_summary', category: 'engagement', label: url });
      const tweetData = await fetchTweetContent(url);
      const { tweetText, profileImage, username } = tweetData;
      setTweetText(tweetText);
      setProfileImage(profileImage);
      setUsername(username);

      // Generate the summary for the current style
      if (style === 'meme') {
        const memeText = await generateMemeText(tweetText);
        const memeImg = await generateMemeImage(memeText);
        
        setSummary(memeText);
        setMemeImage(memeImg);
        setMemeSummary(memeText);
        setMemeImageCache(memeImg);
        setCurrentCardMode('meme'); // Set the current card mode to match the generated content
      } else if (style === 'troll') {
        const trollText = await generateTrollSummary(tweetText);
        setSummary(trollText);
        setTrollSummary(trollText);
        setMemeImage(null);
        setCurrentCardMode('troll'); // Set the current card mode to match the generated content
      } else {
        // Normal mode
        const normalText = await summarizeText(tweetText);
        setSummary(normalText);
        setNormalSummary(normalText);
        setMemeImage(null);
        setCurrentCardMode('normal'); // Set the current card mode to match the generated content
      }

      event({
        action: `${style}_generated`,
        category: 'success',
        label: username,
      });
    } catch (error) {
      console.error("Error generating card:", error);
      alert("Error processing the URL. Please make sure it's a valid X post.");
      event({ action: 'summary_error', category: 'error', label: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyImage = () => {
    copyImageToClipboard();
    event({ action: 'copy_image', category: 'engagement', label: 'desktop' });
  };

  const handleDownloadCard = () => {
    downloadCard();
    event({ action: 'download_image', category: 'engagement', label: currentCardMode });
  };

  const toggleStyle = (newStyle) => {
    setStyle(newStyle);
    // No other changes here - we don't update the card until generate is clicked
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="border-b border-gray-800 p-4">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <div className="flex items-center">
            <svg viewBox="0 0 24 24" aria-hidden="true" className="w-8 h-8 text-white fill-current">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
            </svg>
            <span className="ml-2 text-xl font-bold">Summary</span>
          </div>
          <div className="text-sm text-gray-400">AI-powered post summarizer</div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 md:p-6">
        {/* Original Summary Section */}
        <div className="bg-gray-900 rounded-xl p-4 md:p-6 mb-8 shadow-md">
          <h2 className="text-xl font-bold mb-4">Create X Post Summary</h2>

          <div className="flex justify-end mb-4">
            <div className="flex gap-2">
              <button
                onClick={() => toggleStyle('normal')}
                className={`px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 ${
                  style === 'normal'
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
              >
                Normal
              </button>
              <button
                onClick={() => toggleStyle('troll')}
                className={`px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 ${
                  style === 'troll'
                    ? 'bg-purple-600 text-white shadow-lg'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
              >
                Troll Mode 🤪
              </button>
              <button
                onClick={() => toggleStyle('meme')}
                className={`px-4 py-2 rounded-full font-medium text-sm transition-all duration-200 ${
                  style === 'meme'
                    ? 'bg-green-600 text-white shadow-lg'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
              >
                Meme 😂
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <input
              className="flex-grow bg-black border border-gray-800 p-3 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter X post URL (e.g., https://x.com/username/status/123456789)"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && generateCard()}
            />
            <button
              onClick={generateCard}
              disabled={loading || !url}
              className={`px-6 py-3 rounded-md font-medium transition-colors ${
                loading || !url
                  ? 'bg-gray-700 cursor-not-allowed'
                  : style === 'troll'
                  ? 'bg-purple-500 hover:bg-purple-600'
                  : style === 'meme'
                  ? 'bg-green-500 hover:bg-green-600'
                  : 'bg-blue-500 hover:bg-blue-600'
              }`}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </span>
              ) : (
                `Generate ${style === 'troll' ? 'Troll' : style === 'meme' ? 'Meme' : 'Summary'}`
              )}
            </button>
          </div>
        </div>

        {/* Thread Generator Component */}
        <ThreadGenerator />

        {/* Display generated summary card */}
        {summary && (
          <div className="flex flex-col items-center">
            <div className="w-full overflow-x-auto pb-4 scrollbar-hide">
              <div className="flex justify-center min-w-min">
                <VisualCard
                  profileImage={profileImage}
                  summary={summary}
                  username={username}
                  isTrollMode={style === 'troll'} // Still pass UI mode for consistency
                  isMemeMode={style === 'meme'} // Still pass UI mode for consistency
                  memeImage={memeImage}
                  currentCardMode={currentCardMode} // Pass the current card content mode
                />
              </div>
            </div>

            <div className="mt-6 w-full max-w-md flex flex-col md:flex-row gap-4">
              <button
                onClick={handleCopyImage}
                className="w-full bg-white text-black py-3 px-4 rounded-md font-medium hover:bg-gray-200 transition-colors flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                Copy (Desktop)
              </button>
              <button
                onClick={handleDownloadCard}
                className="w-full bg-gray-800 border border-gray-700 text-white py-3 px-4 rounded-md font-medium hover:bg-gray-700 transition-colors flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download
              </button>
            </div>
          </div>
        )}
      </main>

      <footer className="mt-auto border-t border-gray-800 p-4 text-center text-gray-500 text-sm">
        X-Summary — Quickly summarize X posts with AI {currentCardMode === 'troll' && '👹'} {currentCardMode === 'meme' && '😂'}
      </footer>
    </div>
  );
}

export default App;